/**
 * WebAuthn plumbing for Kratos flows.
 *
 * Kratos ships the ceremony options inside the trigger node's onclick handler
 * (meant for its bundled webauthn.js) and expects the resulting credential back
 * as a JSON string with base64url-encoded buffers. These helpers do exactly what
 * that script does so a React app can drive the ceremony itself.
 */

export interface WebAuthnNode {
  group: string;
  attributes: {
    name?: string;
    value?: unknown;
    onclick?: string;
    [key: string]: unknown;
  };
}

interface KratosWebAuthnOptions {
  publicKey: {
    challenge: string;
    rp?: PublicKeyCredentialRpEntity;
    user?: { id: string; name: string; displayName: string };
    pubKeyCredParams?: PublicKeyCredentialParameters[];
    timeout?: number;
    excludeCredentials?: { id: string; type: string; transports?: string[] }[];
    allowCredentials?: { id: string; type: string; transports?: string[] }[];
    authenticatorSelection?: AuthenticatorSelectionCriteria;
    attestation?: AttestationConveyancePreference;
    userVerification?: UserVerificationRequirement;
    rpId?: string;
  };
}

export function base64UrlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Pulls the options object out of `window.__oryWebAuthn…({...})`. */
export function triggerOptions(node: WebAuthnNode | undefined): KratosWebAuthnOptions | null {
  if (!node) return null;

  const onclick = node.attributes.onclick;
  if (typeof onclick === "string") {
    const start = onclick.indexOf("{");
    const end = onclick.lastIndexOf("}");
    if (start >= 0 && end > start) {
      try {
        const parsed = JSON.parse(onclick.slice(start, end + 1));
        if (parsed?.publicKey) return parsed as KratosWebAuthnOptions;
      } catch {
        // fall through to the value attribute
      }
    }
  }

  const value = node.attributes.value;
  if (value && typeof value === "object" && "publicKey" in value) {
    return value as KratosWebAuthnOptions;
  }
  return null;
}

export function toCreationOptions(
  options: KratosWebAuthnOptions
): PublicKeyCredentialCreationOptions {
  const pk = options.publicKey;
  if (!pk.user || !pk.rp || !pk.pubKeyCredParams) {
    throw new Error("Kratos did not return complete registration options");
  }
  return {
    ...pk,
    rp: pk.rp,
    pubKeyCredParams: pk.pubKeyCredParams,
    challenge: base64UrlToBuffer(pk.challenge),
    user: { ...pk.user, id: base64UrlToBuffer(pk.user.id) },
    excludeCredentials: (pk.excludeCredentials || []).map((c) => ({
      ...c,
      type: "public-key" as const,
      id: base64UrlToBuffer(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
}

export function toRequestOptions(
  options: KratosWebAuthnOptions
): PublicKeyCredentialRequestOptions {
  const pk = options.publicKey;
  return {
    ...pk,
    challenge: base64UrlToBuffer(pk.challenge),
    allowCredentials: (pk.allowCredentials || []).map((c) => ({
      ...c,
      type: "public-key" as const,
      id: base64UrlToBuffer(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
}

export function serializeAttestation(credential: PublicKeyCredential): string {
  const response = credential.response as AuthenticatorAttestationResponse;
  return JSON.stringify({
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64Url(response.attestationObject),
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
    },
  });
}

export function serializeAssertion(credential: PublicKeyCredential): string {
  const response = credential.response as AuthenticatorAssertionResponse;
  return JSON.stringify({
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
    },
  });
}

/** The browser reports a dismissed or timed-out prompt as NotAllowedError. */
export function isUserCancellation(err: unknown): boolean {
  return err instanceof DOMException && err.name === "NotAllowedError";
}
